import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { AdminApplicationStatus, AdminApplicationStatusPost, ApplicationStatusPost } from '../../common/enums/application-status-enum';
import { ApplicationFeedbackModel } from '../../models/ApplicationFeedbackModel';
import { TranslationHelperService } from '../../common/services/translation-helper.service';
import { CustomFieldFormService } from '../../common/services/custom-field-form.service';
import { ApplicationDetail, LabelValue } from '../../models/applicationdetailmodel';
import { DefinitionStageModel } from '../../definitions/models/definition-stage';
import { StageCriteria } from '../../definitions/models/stage-criteria';
import { OrganizationUser } from '../../organization/models/organization-user';
import { AuthService, AuthType } from '../../../../shared/services/auth.service';
import { AssinedApplicationModel } from '../../models/assinged-application-model';
import { GetApplicationListType } from '../../common/enums/get-application-list-type.enum';
import { AdminApplicationViewModel } from '../../applicant-request/models/application-view-model';
import { map } from 'rxjs/operators';
import { CACHE_SKIP_HEADER } from '../../../../shared/inteceptors/http-cache.interceptor';
import { UntypedFormArray } from '@angular/forms';
import { FolderDefinition, FolderDefinitionUsers } from '../../models/folder-definition.model';
import { SubmissionOption } from '../../common/enums/submission-option.enum';
import { NameId } from '../../models/UserResult';
import { ExternalContextModel } from '../../shared-modules-esp/esp-external-shared/models/external-context.model';
import { ApplicationHistory } from '../../models/application-history.model';
import { FieldValue } from '../../models/iframe/definition-filter.model';
import { ApplicationDetailCustomLayout } from '../../shared-modules-esp/form-layout-designer/model/form-layout-designer.model';
import { ApplicationArchivedForm } from '../../models/application-archived-form.model';
import { ApplicationDocument } from '../../models/application-document.model';
import { environment } from 'src/environments/environment';
import { SubmissionInviteModel } from '../../models/submission-invite.model';
import { CustomFieldModel, FieldUniqueCheckModel } from '../../profile-customization/Model/CustomFieldModel';
import { ApplicationDefinition } from '../../definitions/models/application-definition';
import { ImportApplicationsStatus } from '../../models/import-applications-status.model';
import { FillSubmissionFormType } from '../../common/enums/fill-submission-form-type.enum';
import { KeyValueModel } from '../../models/key-value.model';
import { ApplicationStatusesDoughnutModel } from '../../models/application-statuses-doughnut.model';
import { BookingEvent } from 'src/app/requests/esp/shared/booking-calendar-view/model/booking-event.model';
import { cloneApplicationWithoutForm } from '../../common/services/application-object-helper';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  // Create a private BehaviorSubject to hold the shared data
  private linkApplicationDetialsData = new BehaviorSubject<any>(null);
  private applicationsAllData = new BehaviorSubject<any>(null);
  private exportFile = new BehaviorSubject<{ isExporting: boolean, fileName: string }>({ isExporting: false, fileName: '' });
  // Expose the data as an observable for components to subscribe to
  public exportFile$ = this.exportFile.asObservable();
  public linkData$ = this.linkApplicationDetialsData.asObservable();
  public applicationData$ = this.applicationsAllData.asObservable();
  position = signal<{ bottom: number; left: number; width: number } | null>(null);
  // store element reference if needed
  elementRef = signal<HTMLElement | null>(null);
  constructor(private http: HttpClient
    , private _translationHelperService: TranslationHelperService
    , private _authService: AuthService
  ) {

  }

  updateExportFile(value: boolean, fileName: string = '') {
    this.exportFile.next({ isExporting: value, fileName });
  }

  getStatusCountsList(definationId: number): Observable<ApplicationStatusesDoughnutModel> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<ApplicationStatusesDoughnutModel>(`/application/StatusCounts/${definationId}`, options);
  }

  // Call this to reset the state
  resetExportFile() {
    this.exportFile.next({ isExporting: false, fileName: '' });
  }

  updateLinkApplicationData(newData: any): void {
    this.linkApplicationDetialsData.next(newData);
  }
  updateApplicationData(newData: any): void {
    this.applicationsAllData.next(newData);
  }

  getApplicationDetails(applicationId: number, forSingleTab = false, filledFormId: number = 0, revise: boolean = false, refApplicationId: number | string = null, refSCFID: number | string = null): Observable<HttpResponse<any>> {
    let revised = revise ? 'yes' : ''
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');

    let query = `ffId=${filledFormId}&revise=${revised}`;
    if (refApplicationId) {
      query += `&refApplicationId=${refApplicationId}`;
    }
    if (refSCFID) {
      query += `&refSCFID=${refSCFID}`;
    }

    return this.http.get<any>(`/application/detailsv4/${applicationId}/${false}/${false}/${forSingleTab}?${query}`, { observe: 'response', headers: options.headers });
  }

  getApplicationPermissions(applicationId: number): Observable<string[]> {
    return this.http.get<string[]>(`/application/permissions/${applicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getApplicationDetailsByKeyAnonymous(applicationKey: any, code: string): Observable<HttpResponse<any>> {
    return this.http.get<any>(`/application/bykey/${applicationKey}?code=${code}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getAddendum(applicationId: number): Observable<HttpResponse<any>> {
    return this.http.get<any>(`/application/addendum/${applicationId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getSimilarLegalRequestIds(applicationid: any): Observable<HttpResponse<any>> {
    return this.http.get<any>(`/doe/getsimilarlegalRequestIds/${applicationid}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getDefinitionId(): Observable<any> {
    return this.http.get<any>(`/definition/definitionsIcanReassign`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getStages(definitionId: any): Observable<any> {
    return this.http.get<any>(`/definition/stages/${definitionId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getAssignees(obj: any): Observable<any> {
    return this.http.post<Observable<any>>('/application/listV5', obj, this._authService.espOptions);
  }

  getLinkApplicationDetails(applicationId: number): Observable<HttpResponse<any>> {
    return this.http.get<any>('/application/linkedApplicationInfo/' + applicationId, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getActionHistory(applicationId: number): Observable<HttpResponse<any>> {
    return this.http.get<any>('/actionhistory/' + applicationId, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getApplicationsWithPaging(pageNo: number, searchText: string, applicationStatus: number, recordPerPage: number, sortBy: number, isMySpace: boolean, applicantId: string, definitionId: string, linkApplicationId: string = null): Observable<any> {
    return this.http.get<Observable<any>>('/application/list?search=' + searchText + '&filter=' + applicationStatus + '&PageNo=' + pageNo + '&RecordPerPage=' + recordPerPage + '&isMySpace=' + isMySpace + '&sortBy=' + sortBy + '&applicantId=' + applicantId + '&definationId=' + definitionId + "&linkApplicationId=" + linkApplicationId, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getGroupByStages(definitionId: any): Observable<HttpResponse<any>> {
    return this.http.get<any>('/definition/groupByStages/' + definitionId, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getDefinitionsForFilter(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, isRecord = false, survey: string = '', isAll: boolean, forActivities: string = null): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      fieldValues: [],
    }
    if (isAll == true) {
      if (forActivities) {
        return this.http.post<Observable<any>>(`/application/definitionForFilterv2?type=all` + `&forActivities=${forActivities}`,
          searchObject,
          this._authService.espOptions
        );
      }
      else {
        return this.http.post<Observable<any>>(`/application/definitionForFilterv2?type=all`,
          searchObject,
          this._authService.espOptions
        );
      }

    }
    else {
      return this.http.post<Observable<any>>(`/application/definitionForFilterv2?type=${survey ? 'survey' : (isRecord ? 'record' : 'definition')}`,
        searchObject,
        this._authService.espOptions
      );
    }
  }

  getExportHistory(data: any): Observable<HttpResponse<Blob>> {
    const options = {
      headers: this._authService.espOptions?.headers,
      params: this._authService.espOptions?.params,
      observe: 'response' as const,
      responseType: 'blob' as const
    };

    return this.http.post(
      '/data/defApplications/download',
      data,
      options
    ) as Observable<HttpResponse<Blob>>;
  }

  //Obsolete because getting total count effects performance for large data sets
  getApplicationsWithPagingV2(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, defFieldsSearchValues: NameId[] = [], context: ExternalContextModel = null, isSingleTab = false): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      filterContext: context,
      isSingleTab: isSingleTab
    }
    return this.http.post<Observable<any>>('/application/listV4', searchObject, this._authService.espOptions);
  }

  //used with infinite scroll
  getApplicationsWithPagingV5(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, defFieldsSearchValues: NameId[] = [], context: ExternalContextModel | null = null, isSingleTab = false, conditions = [], onlyOverdue = false, encyData: string = '', generalFilters: KeyValueModel[] = []): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses.find(x => x === -1) ? [] : applicationStatuses.filter(x => x !== -1),
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      conditions: context ? context.conditions : [],
      onlyOverdue: onlyOverdue,
      encyData: encyData,
      generalFilters: generalFilters
    }
    return this.http.post<Observable<any>>('/application/listV5', searchObject, this._authService.espOptions);
  }

  //used with infinite scroll
  getApplicationsWithSubmittalRequestInvitation(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, defFieldsSearchValues: NameId[] = [], context: ExternalContextModel = null, isSingleTab = false, conditions = []): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      conditions: context ? context.conditions : []
    }
    return this.http.post<Observable<any>>('/submittalRequest/invitation', searchObject, this._authService.espOptions);
  }

  getApplicationsWithPagingV5Count(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, defFieldsSearchValues: NameId[] = [], context: ExternalContextModel = null, isSingleTab = false, conditions = [], onlyOverdue = false, generalFilters: KeyValueModel[] = []): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses.find(x => x === -1) ? [] : applicationStatuses.filter(x => x !== -1),
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      conditions: context ? context.conditions : [],
      onlyOverdue: onlyOverdue,
      generalFilters: generalFilters
    }
    return this.http.post<Observable<any>>('/application/listV5/count', searchObject, this._authService.espOptions);
  }


  cancelAndAccept(applicationId: number): Observable<any> {
    return this.http.post<Observable<any>>('/application/cancelRelational/' + applicationId, {}, this._authService.espOptions);
  }

  getOpenRequestsForMim(pageNo: number, noOfRecords: number = 12): Observable<{ totalRecords: number, applications: AdminApplicationViewModel[] }> {
    return this.getApplicationsWithPagingV2(pageNo, '', [ApplicationStatusPost.Pending], noOfRecords, 1, false, false, null, null, [], GetApplicationListType.Open);
  }

  getOpenRequestsForMimRequest(pageNo: number, noOfRecords: number = 100, searchText: string): Observable<{ totalRecords: number, applications: AdminApplicationViewModel[] }> {
    return this.getApplicationsWithPagingV2(pageNo, searchText, [ApplicationStatusPost.Accepted, ApplicationStatusPost.Rejected, ApplicationStatusPost.Cancelled], noOfRecords, 1, false, true, null, null, [], GetApplicationListType.Open);
  }

  //obsolete
  getApplicationsAssinged(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId: string, definitionId: string, parentApplicationId: number = 0, definitionIds: number[] = []): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      IsMySpace: isMySpace,
      myApplications: myApplications,
      SortBy: sortBy,
      ApplicantId: applicantId,
      DefinationId: definitionId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1)
    }
    return this.http.post<Observable<any>>('/application/assigned', searchObject, this._authService.espOptions);
  }

  getApplicationListItem(applicationId: number): Observable<HttpResponse<any>> {
    return this.http.get<any>('/application/' + applicationId, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getApplicationStatus(applicationId: number): Observable<number> {
    return this.http.get<number>(`/application/getStatus/${applicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  //http://localhost:55334/webapi/application/list?search=&filter=0&pageNo=1&recordPerPage=10&isMySpace=true&sortBy=0

  dateDifference(submitedDate): string {
    let currDate = new Date;
    let diff = Math.floor(currDate.getTime() - new Date(submitedDate).getTime());
    let day = 1000 * 60 * 60 * 24;

    let days = Math.floor(diff / day);
    let months = Math.floor(days / 31);
    let years = Math.floor(months / 12);

    let message = days.toString();
    if (days == 0) {
      return 'Today'
    }

    if (days == 1) {
      message += ' ' + this._translationHelperService.getTranslationByKey('day');
    } else {
      message += ' ' + this._translationHelperService.getTranslationByKey('days');
    }

    return message;
  }

  reassignApplications(model) {
    return this.http.put('/assessment/reassignAll', model, this._authService.espOptions);
  }

  sendComment(model) {
    return this.http.put('/assessment/comments', model, this._authService.espOptions);
  }
  resendAction(applicationid, model) {
    return this.http.put('/actionhistory/retry/' + applicationid, model, this._authService.espOptions);
  }
  updateAllowSubmission(applicationId, isSubmissionAllowed) {
    return this.http.put('/application/allowLinkedApplicationSubmission/' + applicationId + '/' + isSubmissionAllowed, {}, this._authService.espOptions);
  }

  respondCriteria(model) {
    return this.http.post('/application/respondv2', model, this._authService.espOptions);
  }

  updateDelayJustificationComment(stageId, model) {

    return this.http.put('/assessment/delayJustification/' + stageId, model, this._authService.espOptions);
  }

  getApplicationFeedbackList(applicationId: number, clearCache = false): Observable<ApplicationFeedbackModel[]> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    if (clearCache) {
      options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    }
    return this.http.get<any>('/application/feedback/' + applicationId, options).pipe(
      map(resp => resp)
    );
  }

  getAssessmentAiResults(applicationId: number): Observable<any> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<any>(`/assessment/airesults/${applicationId}`, options);
  }

  getAssessmentDetails(assessmentId: number, clearCache = false): Observable<any> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    if (clearCache) {
      options.headers = options.headers.append(CACHE_SKIP_HEADER, 'true');
    }
    return this.http.get<any>(
      `/assessment/GetAssessedinfov1/${assessmentId}`,
      options
    );
  }

  getIdenediCode(clearCache = false): Observable<string> {
    let url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSP1980031200`;
    if (this._authService.idenediGroupId) {
      url = `${environment.idenediProviderUrl}/api/oauth/AuthorizationCode?client_id=XSP1980031200&gIdenedi=${this._authService.idenediGroupId}`;
    }
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.Idenedi)
    };
    if (clearCache) {
      options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    }
    return this.http.get<any>(url, options).pipe(
      map(code => code)
    );
  }

  addApplicationFeedback(data: any) {
    return this.http.put<any>('/application/comments', data, this._authService.espOptions);
  }

  deleteComment(commentId: number) {
    return this.http.delete<any>('/application/comment?commentId=' + commentId, this._authService.espOptions);
  }

  deleteCommentAttachment(fileGuid: string) {
    return this.http.delete<any>(`/application/commentAttachment?fileGuid=${fileGuid}`, this._authService.espOptions);
  }

  makeApplicationFeedbackVisibleToApplicant(model: any) {
    return this.http.post('/application/feedback/visibletoapplicant/' + model.applicationId, model.feedback, this._authService.espOptions);
  }

  saveCriteriaForm(model: any, assessmentId: number) {
    return this.http.post('/application/assessment/values?assessmentId=' + assessmentId, model, this._authService.espOptions);
  }

  getApplicationSubApplications(applicationId, allowedValuesCriteria): Observable<HttpResponse<any>> {
    return this.http.get<any>('/application/subApplications/' + applicationId + '/' + allowedValuesCriteria, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  applicationAssessmentDisable(applicationId: number, assessmentId: number) {
    return this.http.put('/application/assessment/disable/' + applicationId + '/' + assessmentId, {}, this._authService.espOptions);
  }

  applicationAssessmentEnable(applicationId: number, assessmentId: number) {
    return this.http.put('/application/assessment/enable/' + applicationId + '/' + assessmentId, {}, this._authService.espOptions);
  }

  generateApplicationPDF(applicationId: number, locale: string, applicationNumber: string, filledFormId: number) {
    let params = new HttpParams();
    if (filledFormId > 0) {
      params = params.set('ffid', filledFormId.toString());
    }

    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP),
      observe: 'response' as const,
      responseType: 'blob' as const,
      params
    };

    return this.http.get(`/application/pdf/${applicationId}/${locale}`, options).pipe(
      map(
        data => {
          var file = new Blob([data.body], { type: 'application/pdf' })
          var fileURL = URL.createObjectURL(file);
          window.open(fileURL); // if you want to open it in new tab
          var a = document.createElement('a');
          a.href = fileURL;
          a.target = '_blank';
          a.download = applicationNumber + '.pdf';
          document.body.appendChild(a);
          a.click();
          return data;
        }
      ));
    //environment.baseUrl
    //window.open('http://localhost:55334/webapi/application/pdf/12', '_blank');
  }

  //initial request without any blur event
  private _getCalculatedFieldsPath: string = '/application/getCalculatedValuesV2';
  //field tartgeted request with blur event or section level calculation with section index and section id
  private _getCalculatedFieldsPathV3: string = '/application/getCalculatedValuesV3';
  getCalcuateValues(definition: any, definitionKey: string = null, onBehalfApplicantId: number = 0, resubmit = false, submissionKey: string = '', scfId = null, sectionId = 0, sectionIndex = 0, isRevise = false, forceV3 = false): Observable<any> {
    definition.sectionValues = CustomFieldFormService.mapFormFieldsPostValue(definition.form.sections);

    const defToPost = cloneApplicationWithoutForm(definition);

    const useV3 = ((scfId != null && scfId >= 0) || forceV3) && !resubmit;
    const path = useV3 ? this._getCalculatedFieldsPathV3 : this._getCalculatedFieldsPath;

    const params: string[] = [
      `onBehalf=${encodeURIComponent(String(onBehalfApplicantId))}`,
      `foredit=${resubmit ? 1 : 0}`
    ];

    if (definitionKey) {
      params.push(`definitionKey=${encodeURIComponent(definitionKey)}`);
    }

    params.push(`submissionKey=${encodeURIComponent(submissionKey || '')}`);

    if (useV3) {
      params.push(`forSCFId=${encodeURIComponent(String(scfId))}`);
      params.push(`sectionId=${encodeURIComponent(String(sectionId))}`);
      params.push(`atSectionIndex=${encodeURIComponent(String(sectionIndex))}`);
    }

    if (isRevise) {
      params.push(`isrevise=1`);
    }

    const url = `${path}?${params.join('&')}`;
    return this.http.post<any>(url, defToPost, this._authService.espOptions);
  }

  getCalcuateValuesForCriteria(definition: ApplicationDetail, definitionKey?: string): Observable<any> {
    definition.stages.forEach((stage: DefinitionStageModel) => {
      stage.criteriaList.forEach((criteria: StageCriteria) => {
        if (criteria.form.sections.length > 0) {
          criteria.sectionValues = CustomFieldFormService.mapFormFieldsPostValue(criteria.form.sections);
          //criteria.formValues = CustomFieldFormService.mapFormFieldsPostValueForCriteria(criteria.form.sections[0]);
        }
      });
    });
    const { form: _form, ...defToPost } = JSON.parse(JSON.stringify(definition)) as ApplicationDetail;
    defToPost.stages.flatMap(s => s.criteriaList).forEach(criteria => {
      criteria.form.sections = criteria.form.sections.map(sec => {
        const { nestedSections: _nestedSections, ...sectionWithoutNested } = sec;
        return sectionWithoutNested as typeof sec;
      });
    });
    return this.http.post<any>(this._getCalculatedFieldsPath + `?applicationKey=${definitionKey}`, defToPost, this._authService.espOptions);
  }

  saveReassginOwner(model: any, applicationId: number, newOwnerId: number) {
    return this.http.post('/application/reassign/assessment?applicationId=' + applicationId + '&' + 'newOwnerId=' + newOwnerId, model, this._authService.espOptions);
  }

  checkCustomFieldValueUnique(id: number, value: string, applicationId: number): Observable<FieldUniqueCheckModel> {
    let obj = {
      value: value,
      fieldSCFId: id,
      applicationId: applicationId
    }

    return this.http.put<FieldUniqueCheckModel>('/fieldValue/isUnique', obj, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }


  cancelRequest(applicationId: number, comments: string, notifiedToPersonaIds: number[], notifyApplicant: boolean, notifyInvitee: boolean = false) {
    let submitModel = {
      applicationid: applicationId,
      comments: comments,
      notifiedToPersonaIds: notifiedToPersonaIds,
      notifyApplicant: notifyApplicant,
      notifyInvitee: notifyInvitee
    }
    return this.http.put('/application/cancel', submitModel, this._authService.espOptions);
  }

  reactivateRequest(applicationId: number) {
    return this.http.put(`/application/reactivate?applicationId=${applicationId}`, {}, this._authService.espOptions);
  }

  getApplicationOwners(applicationId: number): Observable<HttpResponse<OrganizationUser[]>> {
    return this.http.get<OrganizationUser[]>(`/assessment/assessors/active?applicationId=${applicationId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getFeeds(dismissed = false): Observable<HttpResponse<{ applications: AssinedApplicationModel[], totalRecords: number }>> {
    return this.http.get<{ applications: AssinedApplicationModel[], totalRecords: number }>('/SubmittalRequest/feeds?dismissed=' + dismissed, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getApplicationFeeds(feedType: SubmissionOption, dismissed = false): Observable<{ applications: AssinedApplicationModel[], totalRecords: number }> {
    if (feedType == SubmissionOption.Assign) {
      return this.http.get<{ applications: AssinedApplicationModel[], totalRecords: number }>('/submittalRequest/feeds/assigned?dismissed=' + dismissed, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) }).pipe(
        map(resp => resp.body)
      );
    } else {
      return this.getFeeds(dismissed).pipe(
        map(resp => resp.body)
      );
    }
  }

  submissionSendInvite(inviteModel: SubmissionInviteModel): Observable<any> {
    return this.http.post(`/submittalRequest/sendInvite`, inviteModel, this._authService.espOptions);
  }

  useSubmissionKey(parentApplicationId: number, submissionKey: string): Observable<any> {
    return this.http.post(`submittalRequest/usesubmissionkey/${parentApplicationId}/${submissionKey}`, {}, this._authService.espOptions);
  }

  dismiss(applicationId: number, applicationType: string): Observable<any> {
    return this.http.get<any>(`/application/dismiss/${applicationId}/${applicationType}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  postQuestion(applicationId: number, question: StageCriteria): Observable<any> {
    return this.http.post(`/application/question?applicationId=${applicationId}`, question, this._authService.espOptions);
  }

  openStage(applicationId: number, stageId: number, assessmentId: number, comments: string): Observable<any> {
    let model = {
      applicationId: applicationId,
      stageToMoveId: stageId,
      assessmentId: assessmentId,
      comments: comments
    }
    return this.http.post(`/application/openStage`, model, this._authService.espOptions);
  }

  openStageComments(applicationId: number, stageId: number): Observable<any> {
    return this.http.get(`/application/openStageComments/${applicationId}/${stageId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) }).pipe(
      map(resp => resp.body)
    );
  }
  setRequestSubmitters(applicationId, childApplicants: any, notifySubmitters: boolean): Observable<any> {
    let endPoint = '/submittalRequest/SubmissionsSubmitters';
    let model = {
      applicationId: applicationId,
      allowedSubmitters: childApplicants,
      notifySubmitter: notifySubmitters
    }
    return this.http.put<any>(endPoint, model, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getFolderReferenceApplications(applicationId: number, definitionId: number): Observable<FolderDefinition[]> {
    return this.http.get<any>(`/application/referenceApplications?applicationId=${applicationId}&definitionId=${definitionId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) }).pipe(
      map(resp => resp.body)
    );
  }

  getFolderReferenceApplicationsBasicInfo(applicationId: number, definitionId: number): Observable<FolderDefinition[]> {
    return this.http.get<any>(`/refapplications/all/${applicationId}/${definitionId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getFolderReferenceApplicationsByDefIf(applicationId: number, referredDefinitionId: number, refernceSCFId: number, page: number = 0): Observable<FolderDefinition> {
    let query = '';
    if (page > 0) {
      query = `?pageNo=${page}`
    }
    return this.http.get<any>(`/refapplications/details/${applicationId}/${referredDefinitionId}/${refernceSCFId}${query}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  postFolderReferenceApplicationsByDefIf(applicationId: number, referredDefinitionId: number, refernceSCFId: number, page: number, search: string, status: AdminApplicationStatus): Observable<FolderDefinition> {
    let payload = {
      applicationId: applicationId,
      referedDefinitionId: referredDefinitionId,
      refernceSCFId: refernceSCFId,
      pageNo: page,
      isAll: status == AdminApplicationStatus.All,
      search: search
    }
    return this.http.post<any>(`/refapplications/detailsV2`, payload, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getAllowedUsers(refDefId: number): Observable<FolderDefinitionUsers> {
    return this.http.get<any>(`/refapplications/allowedUsers/${refDefId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  uploadDocument(data: FormData) {
    return this.http.post('/document', data, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  updateDocument(data: FormData) {
    return this.http.put('/document', data, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  deleteDocument(docId: number) {
    return this.http.delete('/document?id=' + docId, { headers: this._authService.buildAuthHeader(AuthType.ESP) })

  }
  getApplicationDocuments(applicationId: string, search: string = ''): Observable<ApplicationDocument[]> {
    return this.http.post<ApplicationDocument[]>('/document/get', { search: search, holderId: applicationId }, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  getApplicationReopen(applicationId: string, Comments: string = ''): Observable<any> {
    return this.http.post<any[]>('/application/Reopen', { ApplicationId: applicationId, Comments: Comments }, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  ChangeApplicant(applicationId, newApplicantId): Observable<HttpResponse<any>> {
    return this.http.get<any>(`/application/ChangeApplicant?applicationId=${applicationId}&newApplicantId=${newApplicantId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  deleteApplication(id: number) {
    return this.http.delete('/record/' + id, { headers: this._authService.buildAuthHeader(AuthType.ESP) })

  }
  archivedApplication(id: number) {
    return this.http.put('/record/Archived/' + id, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) })

  }
  undoDeleteArchive(id: number) {
    return this.http.put('/record/Undoarchived/' + id, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  pauseApplication(id: number){
    return this.http.put('/application/pause?applicationId=' + id, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  resumeApplication(id: number){
    return this.http.put('/application/resume?applicationId=' + id, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  applicationHistory(applicationId: number) {
    return this.http.get<ApplicationHistory[]>('/application/history/' + applicationId, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  applicationisLock(applicationId: number, isLock: boolean) {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<any>(`/application/lock/${applicationId}/${isLock}`, options);
  }

  getAnonKey(applicationId: number): Observable<string> {
    return this.http.get<string>('/application/key/' + applicationId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  shareWith(model: { applicationId: number, idenediIds: string[] }) {
    return this.http.put('/application/shareWith', model, this._authService.espOptions);
  }

  getSharedWith(applicationId: number): Observable<string[]> {
    return this.http.get<string[]>('/application/shareWith/' + applicationId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getSharedWithV2(applicationId: number): Observable<any[]> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<any>('/application/shareWithV2/' + applicationId, options);
  }
  updatepermission(userIdenedi: string, applicationId: number, permission: string): Observable<any> {
    return this.http.put<any>(`/application/shareWith/updatepermission?userIdenedi=${userIdenedi}&applicationid=${applicationId}&permission=${permission}`, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  removeSharedUser(userIdenedi: string, applicationId: number): Observable<any> {
    return this.http.delete(`application/shareWith/${userIdenedi}?applicationId=${applicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  canAccessParentApplication(parentApplicationId: number): Observable<boolean> {
    return this.http.get<boolean>(`application/canaccess/${parentApplicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getSingleApplicationStatusList(definitionId: number): Observable<any> {
    return this.http.get<any>(`applicationstatuses/forDefinitionv2/${definitionId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getApplicationsWithV3(childDefinitionId: number, pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], defFieldsSearchValues: NameId[] = [], fieldValues: FieldValue[] = [], context: ExternalContextModel = null, isSingleTab = false, invitationsEnabled: boolean = false): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      // type: applicationListType,
      // SortBy: sortBy,
      ApplicantId: applicantId,
      // Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      fieldValues: fieldValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      invitationView: invitationsEnabled
    }
    return this.http.post<Observable<any>>(`/submittalRequest/feedsV3/${childDefinitionId}?invitationView=${invitationsEnabled}`, searchObject, this._authService.espOptions);

  }
  getApplicationsWithV4(childDefinitionId: number, pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], defFieldsSearchValues: NameId[] = [], fieldValues: FieldValue[] = [], context: ExternalContextModel = null, isSingleTab = false, invitationsEnabled: boolean = false): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses,
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      // type: applicationListType,
      // SortBy: sortBy,
      ApplicantId: applicantId,
      // Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      fieldValues: fieldValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      invitationView: invitationsEnabled
    }
    return this.http.post<Observable<any>>(`/submittalRequest/feedsV4/${childDefinitionId}?invitationView=${invitationsEnabled}`, searchObject, this._authService.espOptions);

  }

  forCustomForm(applicationId: number): Observable<ApplicationDetailCustomLayout> {
    return this.http.get<ApplicationDetailCustomLayout>(`application/forCustomForm/${applicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getApplicationVersionsList(applicationId: number): Observable<ApplicationArchivedForm[]> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<ApplicationArchivedForm[]>(`application/archivedVersions/${applicationId}`, options);
  }

  returnApplication(applicationId: number, comments: string) {
    let model = {
      applicationId: applicationId,
      comments: comments
    }
    return this.http.put('application/return', model, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  saveReassignV2(model: any) {
    return this.http.put('/assessment/reassignV2', model, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getAskaquestionUsers(definitionId: number, applicationDetails: ApplicationDefinition): Observable<any> {
    // To avoid mutating the original applicationDetails by reference,
    // create a shallow copy and remove the 'form' property if it exists.
    let applicationDetailsCopy: any;
    if (applicationDetails && typeof applicationDetails === 'object') {
      applicationDetailsCopy = { ...applicationDetails };
      if ('form' in applicationDetailsCopy) {
        delete applicationDetailsCopy.form;
      }
    } else {
      applicationDetailsCopy = applicationDetails;
    }
    return this.http.post<any>('/application/askaquestionUsers/' + definitionId, applicationDetailsCopy, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getAssigntoUsers(definitionId: number, applicationDetails: ApplicationDefinition): Observable<any> {
    // To avoid mutating the original applicationDetails by reference,
    // create a shallow copy and remove the 'form' property if it exists.
    let applicationDetailsCopy: any;
    if (applicationDetails && typeof applicationDetails === 'object') {
      applicationDetailsCopy = { ...applicationDetails };
      if ('form' in applicationDetailsCopy) {
        delete applicationDetailsCopy.form;
      }
    } else {
      applicationDetailsCopy = applicationDetails;
    }
    return this.http.post<any>('/application/assigntoUsers/' + definitionId, applicationDetailsCopy, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getApplicantContactsInfo(applicantId: number) {
    return this.http.get<LabelValue[]>('/applicant/contactsInfo/' + applicantId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  sendReminder(assessmentId: number) {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<LabelValue[]>('/application/sendRemider/' + assessmentId, options);
  }
  deactivateApplication(applicationId: number, model = null) {
    return this.http.post('record/deactivate/' + applicationId, model, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  activateApplication(applicationId: number, model = null) {
    return this.http.post('record/activate/' + applicationId, model, { headers: this._authService.buildAuthHeader(AuthType.ESP) })
  }
  checkPaymentStatus(applicationNumber: string): Observable<any> {
    return this.http.get<LabelValue[]>(`/application/checkpaymentstatus/${applicationNumber}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  paymentProcessed(applicationNumber: string): Observable<any> {
    return this.http.get<LabelValue[]>(`/application/paymentprocessed/${applicationNumber}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  invitables(definitionId: number, applicationId: number, stageId: number, pageNo: number, pageSize: number, search: string): Observable<{ items: AdminApplicationViewModel[], total: number }> {
    return this.http.get<{ items: AdminApplicationViewModel[], total: number }>(`/submittalRequest/invitables/${definitionId}/${applicationId}/${stageId}?pageNo=${pageNo}&pageSize=${pageSize}&search=${search}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  invited(definitionId: number, applicationId: number, stageId: number): Observable<{ isAll: boolean, items: AdminApplicationViewModel[], total: number }> {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<{ isAll: boolean, items: AdminApplicationViewModel[], total: number }>(`/submittalRequest/invited/${definitionId}/${applicationId}/${stageId}`, options);
  }
  moveUp(applicantionId: number) {
    return this.http.get<any>('/application/setorder/moveup/' + applicantionId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  moveDown(applicantionId: number) {
    return this.http.get<any>('application/setorder/movedown/' + applicantionId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  reSendInvite(parentApplicationId, inviteeId) {
    const options = {
      headers: this._authService.buildAuthHeader(AuthType.ESP)
    };
    options.headers = options.headers.append(CACHE_SKIP_HEADER, '');
    return this.http.get<any>(`submittalRequest/resendinvite/${parentApplicationId}/${inviteeId}`, options);
  }
  copySubmissionlink(parentApplicationId, inviteeId) {
    return this.http.get<any>(`submittalRequest/submissionlink/${parentApplicationId}/${inviteeId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });

  }
  getDefinitionData(file: any, definitionId: number, sectionId: number, type: FillSubmissionFormType): Observable<any> {
    return this.http.post<any>(`upload/getDefinitionData/${definitionId}/${sectionId}?type=${type}`, file, this._authService.espOptions);
  }
  getDefinitionDataMultiSection(file: any, definitionId: number, sectionId: number, type: FillSubmissionFormType): Observable<any> {
    return this.http.post<any>(`upload/getDefinitionDataMultiSection/${definitionId}/${sectionId}?type=${type}`, file, this._authService.espOptions);
  }
  revokeInvite(inviteeId, parentApplicationId): Observable<HttpResponse<any>> {
    return this.http.get<any>(`submittalRequest/invitation/revoke/${inviteeId}/${parentApplicationId}`, { observe: 'response', headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  uploadSectionFile(formData: FormData, applicationId: number, sectionId: number) {
    return this.http.post<any>(`upload/keepFile/${applicationId}/${sectionId}`, formData, this._authService.espOptions);
  }
  getAIuploadedFiles(applicationId: number): Observable<any> {
    return this.http.get<any>('application/KeptSectionFiles/' + applicationId, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  /**
   * Key is stage name and value is count of applications in that stage
   * @param definitionIds
   * @returns
   */
  stagesWithCount(pageNo: number, searchText: string, applicationStatuses: number[], recordPerPage: number, sortBy: number, isMySpace: boolean, myApplications: boolean, applicantId?: number, parentApplicationId: number = 0, definitionIds: number[] = [], applicationListType: GetApplicationListType = GetApplicationListType.Open, defFieldsSearchValues: NameId[] = [], context: ExternalContextModel = null, isSingleTab = false, conditions = [], onlyOverdue = false, encyData: string = ''): Observable<any> {
    const searchObject = {
      Search: searchText,
      Statuses: applicationStatuses.find(x => x === -1) ? [] : applicationStatuses.filter(x => x !== -1),
      PageNo: pageNo,
      RecordPerPage: recordPerPage,
      //IsMySpace: isMySpace,
      //myApplications: myApplications,
      type: applicationListType,
      SortBy: sortBy,
      ApplicantId: applicantId,
      Categoreis: [],
      ParentApplicationId: parentApplicationId,
      definitionIds: definitionIds.find(x => x === -1) ? [] : definitionIds.filter(x => x !== -1),
      searchValues: defFieldsSearchValues,
      filterContext: context,
      isSingleTab: isSingleTab,
      conditions: context ? context.conditions : [],
      onlyOverdue: onlyOverdue,
      encyData: encyData
    }
    return this.http.post<any>('/application/StagesWithCount', searchObject, this._authService.espOptions);
  }

  submitApplication(applicationId: number): Observable<HttpResponse<any>> {
    return this.http.post<any>(`application/sets/submitv1/${applicationId}`, {}, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }

  getUsers(applicantionId: number): Observable<any> {
    return this.http.get<any>(`application/accessibleUsersV1/${applicantionId}`,
      { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }



  setPosition(pos: { bottom: number; left: number; width: number }) {
    this.position.set(pos);
  }

  setElement(el: HTMLElement) {
    this.elementRef.set(el);
  }

  closeAsRequest(applicationId: number, comments: string, notifiedToPersonaIds: number[], notifyApplicant: boolean, notifyInvitee: boolean, closeAs: string) {
    let submitModel = {
      applicationid: applicationId,
      comments: comments,
      notifiedToPersonaIds: notifiedToPersonaIds,
      notifyApplicant: notifyApplicant,
      notifyInvitee: notifyInvitee,
      closeAs: closeAs
    }
    return this.http.put('/application/closeAs', submitModel, this._authService.espOptions);
  }
  getTimeLimitedLink(applicationid: number, expireson: string): Observable<any> {
    return this.http.get<any>(`application/timebasesharelink/${applicationid}/${expireson}`,
      { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getApplicationName(applicationId: number): Observable<any> {
    return this.http.get<any>(`application/name/${applicationId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getDefinitionName(definitionId: number): Observable<any> {
    return this.http.get<any>(`definition/name/${definitionId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getBookings(definitionId: number, fieldValue: number): Observable<any> {
    return this.http.get<any>(`/booking/list/${fieldValue}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  IsOverlapping(data: { definitionId: number, start: Date, end: Date, bookingForId: number }): Observable<any> {
    return this.http.post<any>(`/booking/IsOverlapping`, data, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getDaysOff(fieldValue: number): Observable<any> {
    return this.http.get<any>(`/booking/offdays/${fieldValue}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  postDaysOff(fieldValue: number, daysOff: number[]): Observable<any> {
    return this.http.post<any>(`/booking/offdays/${fieldValue}`, daysOff, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  getBookingAmIOwner(bookingId: number): Observable<any> {
    return this.http.get<any>(`booking/amIOwner/${bookingId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  postBooking(booking: BookingEvent): Observable<any> {
    return this.http.post<any>(`/booking`, booking, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  deleteBooking(bookingId: number): Observable<any> {
    return this.http.delete<any>(`/booking/${bookingId}`, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
  updateBooking(booking: BookingEvent): Observable<any> {
    return this.http.put<any>(`/booking`, booking, { headers: this._authService.buildAuthHeader(AuthType.ESP) });
  }
}
